import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ExpenseCaptureForm from '../components/expenses/ExpenseCaptureForm';
import { isCrmRole } from '../lib/roles';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { useAuth } from '../contexts/AuthContext';
import { EXPENSES_QUERY_KEY } from './ExpenseApprovalScreen';

/**
 * `/expenses/new` — an office-app user files their OWN receipt (FE-1).
 *
 * Who this is for: anyone in the office tree, including `logistics` and
 * `supervisor`, who get the office app but are not in the field tree that
 * `/field/expenses/new` lives in. TNT's salesperson is `logistics` and had no
 * way to file at all.
 *
 * The form is the field tree's, shared (ExpenseCaptureForm), in `mode="office"`:
 * it adds "On a job" | "General / sales", picks from the company's jobs rather
 * than a crew's, and offers an optional customer on a general receipt. A filed
 * receipt lands on `/expenses` with "Mine" selected, so the filer sees it.
 *
 * Online-only, like the field screen — there is no SyncMutation entity for an
 * expense, by decision. Offline, the form says so and the File button is off; a
 * failed write prints the API's reason. Nothing is dropped silently.
 */
export default function OfficeExpenseCaptureScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return (
    <div data-testid="office-expense-capture" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Expenses"
        title="Add receipt"
        description="Photograph a receipt — for a job, or a general / sales expense."
        onBack={() => navigate(-1)}
        backLabel="Back"
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <ExpenseCaptureForm
          mode="office"
          currentUserId={user?.id ?? null}
          // GET /sales-activities is CRM_ROLES only; anyone else gets no picker.
          meetingPicker={isCrmRole(user?.role)}
          className="mx-auto max-w-[1920px] space-y-4"
          onFiled={() => {
            queryClient.invalidateQueries({ queryKey: EXPENSES_QUERY_KEY });
            navigate('/expenses?mine=1', { replace: true });
          }}
        />
      </section>
    </div>
  );
}
